import React, { useState, useEffect } from 'react';
import { useQuery, useAction, getGlobalConfig, updateGlobalConfig } from 'wasp/client/operations';
import { useAuth } from 'wasp/client/auth';
import { Link } from 'react-router-dom';
import DefaultLayout from '../../layout/DefaultLayout';
import { Loader2, Save, Wrench, Info, AlertTriangle, ShieldCheck, Settings2, Globe, MapPin } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';

const GlobalSettingsPage: React.FC = () => {
    const { data: user, isLoading: isAuthLoading } = useAuth();
    const { data: config, isLoading: isConfigLoading } = useQuery(getGlobalConfig);
    const updateConfig = useAction(updateGlobalConfig);
    const { toast } = useToast();

    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const [maintenanceMessage, setMaintenanceMessage] = useState('');
    const [registrationEnabled, setRegistrationEnabled] = useState(true);
    const [brandName, setBrandName] = useState('Gedoba');
    const [brandSubtitle, setBrandSubtitle] = useState('Building Portal');
    const [supportEmail, setSupportEmail] = useState('');
    const [vertical, setVertical] = useState('mixed');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (config) {
            setMaintenanceMode(config.maintenanceMode);
            setMaintenanceMessage(config.maintenanceMessage || '');
            setRegistrationEnabled(config.registrationEnabled);
            setBrandName(config.brandName || 'Gedoba');
            setBrandSubtitle(config.brandSubtitle || 'Building Portal');
            setSupportEmail(config.supportEmail || '');
            setVertical(config.vertical || 'mixed');
        }
    }, [config]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            await updateConfig({
                maintenanceMode,
                maintenanceMessage,
                registrationEnabled,
                brandName,
                brandSubtitle,
                supportEmail: supportEmail || null,
                vertical,
            });
            toast({
                title: 'Settings updated',
                description: 'Global settings have been successfully saved.',
            });
        } catch (error: any) {
            toast({
                title: 'Error updating settings',
                description: error.message || 'Something went wrong',
                variant: 'destructive',
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (isAuthLoading || isConfigLoading) {
        return (
            <div className="flex h-96 w-full items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!user) {
        return null;
    }

    return (
        <DefaultLayout user={user}>
            <div className="mx-auto max-w-4xl p-4 md:p-6 lg:p-8">
                <div className="mb-8 flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Global Settings</h1>
                        <p className="mt-2 text-slate-500 dark:text-slate-400">
                            Manage platform-wide configurations and maintenance mode.
                        </p>
                    </div>
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex items-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-primary/20 hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-50 transition-all active:scale-95"
                    >
                        {isSaving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Save className="h-4 w-4" />
                        )}
                        Save Changes
                    </button>
                </div>

                <div className="grid gap-8">
                    {/* Instance branding / vertical */}
                    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="border-b border-slate-200 bg-slate-50/50 p-6 dark:border-slate-800 dark:bg-slate-800/50">
                            <div className="flex items-center gap-3">
                                <div className="rounded-lg bg-sky-100 p-2 dark:bg-sky-900/30">
                                    <Globe className="h-5 w-5 text-sky-600 dark:text-sky-400" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-slate-900 dark:text-white">Instance Branding</h2>
                                    <p className="text-sm text-slate-500">
                                        Configure this deployment for any building vertical (mall, residence, campus, coworking, etc.).
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="p-6 grid gap-4 md:grid-cols-2">
                            <div>
                                <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">Brand name</label>
                                <input
                                    value={brandName}
                                    onChange={(e) => setBrandName(e.target.value)}
                                    className="w-full rounded-xl border border-slate-200 bg-white p-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-slate-800 dark:bg-slate-950"
                                    placeholder="Gedoba"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">Subtitle</label>
                                <input
                                    value={brandSubtitle}
                                    onChange={(e) => setBrandSubtitle(e.target.value)}
                                    className="w-full rounded-xl border border-slate-200 bg-white p-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-slate-800 dark:bg-slate-950"
                                    placeholder="Building Portal"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">Support email</label>
                                <input
                                    type="email"
                                    value={supportEmail}
                                    onChange={(e) => setSupportEmail(e.target.value)}
                                    className="w-full rounded-xl border border-slate-200 bg-white p-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-slate-800 dark:bg-slate-950"
                                    placeholder="support@example.com"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                                  Building system pack
                                </label>
                                <select
                                    value={vertical}
                                    onChange={(e) => setVertical(e.target.value)}
                                    className="w-full rounded-xl border border-slate-200 bg-white p-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-slate-800 dark:bg-slate-950"
                                >
                                    <option value="education">Educational institution (Student / Academics / Campus / Marketplace)</option>
                                    <option value="skyscraper">Skyscraper / tower (Community / Business / Management)</option>
                                    <option value="residential">Residential / apartments</option>
                                    <option value="retail">Retail / shopping mall</option>
                                    <option value="coworking">Coworking / innovation</option>
                                    <option value="community">Community center</option>
                                    <option value="mixed">Mixed / multi-purpose</option>
                                    <option value="custom">Custom (starts from skyscraper tabs)</option>
                                </select>
                                <p className="mt-1 text-xs text-slate-500">
                                  Controls which top-level view tabs and sidebar modules this instance provisions.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Maintenance Mode Section */}
                    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="border-b border-slate-200 bg-slate-50/50 p-6 dark:border-slate-800 dark:bg-slate-800/50">
                            <div className="flex items-center gap-3">
                                <div className="rounded-lg bg-amber-100 p-2 dark:bg-amber-900/30">
                                    <Wrench className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-slate-900 dark:text-white">Maintenance Mode</h2>
                                    <p className="text-sm text-slate-500">Take the site offline for updates or repairs.</p>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 space-y-6">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                    <label className="text-sm font-semibold text-slate-900 dark:text-white">Enable Maintenance Mode</label>
                                    <p className="mt-1 text-sm text-slate-500">
                                        When active, normal users will be redirected to a maintenance page. Admins can still access the site.
                                    </p>
                                </div>
                                <button
                                    onClick={() => setMaintenanceMode(!maintenanceMode)}
                                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${maintenanceMode ? 'bg-primary' : 'bg-slate-200 dark:bg-slate-700'
                                        }`}
                                >
                                    <span
                                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${maintenanceMode ? 'translate-x-5' : 'translate-x-0'
                                            }`}
                                    />
                                </button>
                            </div>

                            {maintenanceMode && (
                                <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                    <label className="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
                                        Maintenance Message
                                    </label>
                                    <textarea
                                        value={maintenanceMessage}
                                        onChange={(e) => setMaintenanceMessage(e.target.value)}
                                        placeholder="Briefly explain why the site is offline..."
                                        className="w-full rounded-xl border border-slate-200 bg-white p-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-slate-800 dark:bg-slate-950 min-h-[100px]"
                                    />
                                    <div className="mt-2 flex items-start gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/10 p-3 rounded-lg border border-amber-100 dark:border-amber-900/20">
                                        <Info className="h-4 w-4 shrink-0" />
                                        <p>This message will be visible to all visitors while maintenance mode is active.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* User Registration Section */}
                    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
                        <div className="border-b border-slate-200 bg-slate-50/50 p-6 dark:border-slate-800 dark:bg-slate-800/50">
                            <div className="flex items-center gap-3">
                                <div className="rounded-lg bg-indigo-100 p-2 dark:bg-indigo-900/30">
                                    <ShieldCheck className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-slate-900 dark:text-white">User Registration</h2>
                                    <p className="text-sm text-slate-500">Control if new users can sign up to the platform.</p>
                                </div>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                    <label className="text-sm font-semibold text-slate-900 dark:text-white">Allow New Registrations</label>
                                    <p className="mt-1 text-sm text-slate-500">
                                        When disabled, signup links will be hidden and the signup page will be inaccessible. Existing users can still log in.
                                    </p>
                                </div>
                                <button
                                    onClick={() => setRegistrationEnabled(!registrationEnabled)}
                                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${registrationEnabled ? 'bg-primary' : 'bg-slate-200 dark:bg-slate-700'
                                        }`}
                                >
                                    <span
                                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${registrationEnabled ? 'translate-x-5' : 'translate-x-0'
                                            }`}
                                    />
                                </button>
                            </div>
                        </div>
                    </section>

                    {/* Others Section */}
                    <section>
                        <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                            <ShieldCheck className="h-5 w-5 text-primary" />
                            Platform Configuration
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <Link
                                to="/admin/section-visibility"
                                className="group flex flex-col p-6 rounded-2xl border border-slate-200 bg-white shadow-sm hover:border-primary/50 hover:shadow-md transition-all dark:border-slate-800 dark:bg-slate-900"
                            >
                                <div className="mb-4 rounded-xl bg-blue-100 p-3 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 w-fit group-hover:scale-110 transition-transform">
                                    <Settings2 className="h-6 w-6" />
                                </div>
                                <h3 className="font-bold text-slate-900 dark:text-white mb-1">Section Visibility</h3>
                                <p className="text-sm text-slate-500">Enable or disable features globally across all locations.</p>
                            </Link>

                            <Link
                                to="/admin/domains"
                                className="group flex flex-col p-6 rounded-2xl border border-slate-200 bg-white shadow-sm hover:border-primary/50 hover:shadow-md transition-all dark:border-slate-800 dark:bg-slate-900"
                            >
                                <div className="mb-4 rounded-xl bg-emerald-100 p-3 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400 w-fit group-hover:scale-110 transition-transform">
                                    <Globe className="h-6 w-6" />
                                </div>
                                <h3 className="font-bold text-slate-900 dark:text-white mb-1">Registration Domains</h3>
                                <p className="text-sm text-slate-500">Whitelist or blacklist email domains for new user signups.</p>
                            </Link>

                            <Link
                                to="/admin/geoblocking"
                                className="group flex flex-col p-6 rounded-2xl border border-slate-200 bg-white shadow-sm hover:border-primary/50 hover:shadow-md transition-all dark:border-slate-800 dark:bg-slate-900"
                            >
                                <div className="mb-4 rounded-xl bg-purple-100 p-3 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400 w-fit group-hover:scale-110 transition-transform">
                                    <MapPin className="h-6 w-6" />
                                </div>
                                <h3 className="font-bold text-slate-900 dark:text-white mb-1">Geo-Blocking</h3>
                                <p className="text-sm text-slate-500">Restrict access to the platform based on geographic countries.</p>
                            </Link>
                        </div>
                    </section>

                    {/* Site Status Overview */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                            <div className="flex items-center gap-3 mb-4">
                                <ShieldCheck className="h-5 w-5 text-emerald-500" />
                                <h3 className="font-bold text-slate-900 dark:text-white">Admin Access</h3>
                            </div>
                            <p className="text-sm text-slate-500 mb-4">
                                You are currently logged in as an administrator. Even if maintenance mode is enabled, you will have full access to all features to perform management tasks.
                            </p>
                            <div className="text-xs font-mono bg-slate-100 dark:bg-slate-800 p-2 rounded text-slate-600 dark:text-slate-400">
                                Admin Bypass: ACTIVE
                            </div>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
                            <div className="flex items-center gap-3 mb-4">
                                <AlertTriangle className="h-5 w-5 text-rose-500" />
                                <h3 className="font-bold text-slate-900 dark:text-white">Critical Actions</h3>
                            </div>
                            <p className="text-sm text-slate-500 mb-4">
                                Changes to global settings take effect immediately across the entire platform. Please verify all messages before saving.
                            </p>
                            <button
                                onClick={() => { }}
                                className="text-xs font-semibold text-rose-500 hover:text-rose-600 transition-colors"
                                title="Only if needed"
                            >
                                Clear Platform Cache
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </DefaultLayout>
    );
};

export default GlobalSettingsPage;
